/* The venue rows, as the desktop dock or the mobile list view.
 *
 * Split out from `g-map` because the workspace's job is already large — it owns
 * the data, the filters, the API calls and the map's imperative surface. This
 * component owns one thing: the scrolling list. The chip band and the
 * follow-the-camera switch both live outside it.
 *
 * Presentational, with one exception noted at `scrollRowIntoView`: selecting a
 * venue on the map has to bring its row into view, and only the element that
 * scrolls can do that.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { shadowReset } from '../../design/base.js';
import type { Venue } from '../../core/types.js';
import '../../component/g-notice/g-notice.js';
import './place-row.js';

@customElement('map-panel')
export class MapPanel extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: flex;
        flex-direction: column;
        /* 400, not 380: the five kind chips measure ~372px together, and at 380
           the last one sat half off the scrollable row where nobody would look
           for it. Chips that all fit are worth 20px of map. */
        width: 400px;
        height: 100%;
        flex: none;
        border-right: 1px solid var(--border);
        background: var(--bg);
      }
      /* The only scrollbar the app shows. Everything else either doesn't scroll
         (Map fills the outlet) or hides its bar deliberately (g-chips), so the
         platform default — a bright grey slab in dark mode — is jarring here,
         sitting a few pixels from the venue rows.

         Both syntaxes are declared on purpose. Chrome 121+ and Firefox honour
         the standard properties and ignore the ::-webkit- rules entirely;
         older WebKit does the reverse. Either way the bar is themed, and the
         two are tuned to look the same.

         The thumb is --border at rest, one step up from the background: present
         when you look for it, invisible when you don't (§31). --text-dim on
         hover confirms it's grabbable. */
      .rows {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: thin;
        scrollbar-color: var(--border) transparent;
      }
      .rows::-webkit-scrollbar {
        width: 10px;
      }
      .rows::-webkit-scrollbar-track {
        background: transparent;
      }
      /* WebKit draws stepper arrows at the ends of a scrollbar by default.
         Verified no-op in Chrome — its stepper survives this rule and every
         variant of it, so the small arrow at the top of the track is Chrome's
         and is not removable from CSS. It does pick up the colours above, which
         is why it is left alone rather than fought. Kept for Safari, where the
         rule does apply. */
      .rows::-webkit-scrollbar-button {
        display: none;
      }
      /* The transparent border plus background-clip is what insets the thumb
         from the panel edge — a webkit thumb has no margin of its own. */
      .rows::-webkit-scrollbar-thumb {
        border: 3px solid transparent;
        border-radius: var(--radius-full);
        background: var(--border);
        background-clip: content-box;
      }
      .rows::-webkit-scrollbar-thumb:hover {
        background: var(--text-dim);
        background-clip: content-box;
      }
      .empty {
        padding: var(--space-5) var(--space-3);
      }
      /* Mobile list mode (ADR 0017). The panel keeps its one real job — the
         scrolling rows — while the chip band floats above it and the follow toggle
         is gone entirely (ADR 0012 decision 9).

         Opaque, because the list COVERS the map rather than sitting beside it: the
         canvas stays laid out underneath so it never has to be told its size
         changed, and anything see-through here would show pins behind the rows. */
      :host([mobile]) {
        position: relative;
        width: 100%;
        border-right: 0;
        background: var(--bg);
      }
      /* The band overlays the rows rather than stacking above them, so hiding it
         reveals content instead of leaving a gap — which means the first row needs
         the band's height back. --band-h is measured by g-map; the fallback is the
         touch figure. Room at the foot is for the floating view toggle.

         --safe-t on top of that: Map is a fill workspace, so the shell's status-bar
         padding is cancelled and this scroller starts at true y=0. The band pays
         that inset back for itself (it sits at top: --safe-t) but --band-h is its
         offsetHeight alone, so without this the first row clears the band and not
         the status bar. 0px wherever there is no inset, which is every device the
         list was tested on before an iPhone. */
      :host([mobile]) .rows {
        padding-top: calc(var(--safe-t) + var(--band-h, 61px));
        padding-bottom: 76px;
      }
      /* An opaque cap over the status-bar strip, because padding alone only fixes
         the resting state: the band collapses clear of the inset on scroll-down
         ("nothing lingers in the strip above them"), and once it is gone the rows
         travel through that strip with nothing over them. The canvas view wants
         exactly that and washes it with a gradient instead — this substrate is
         opaque, so only its background may show here, never a row. */
      :host([mobile])::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1;
        height: var(--safe-t);
        background: var(--bg);
        pointer-events: none;
      }
      @media (max-width: 959px) {
        :host {
          width: 100%;
          border-right: 0;
        }
      }
    `,
  ];

  @property({ attribute: false }) venues: Venue[] = [];
  @property({ attribute: false }) selected: number | null = null;
  /** Set while the venue list is still in flight, so the panel says so. */
  @property({ type: Boolean }) loading = false;
  /**
   * Whether the list is following the camera. Not a control here — the switch
   * lives over the map — but an empty list means two different things depending
   * on it, and only the wording can say which.
   */
  @property({ type: Boolean }) follow = true;
  /**
   * Rendering as the mobile list view rather than as the desktop dock.
   *
   * Reflected because the styles above key off it. Set by the workspace rather
   * than derived from a media query here: only the thing choosing between the two
   * arrangements knows which one this is.
   */
  @property({ type: Boolean, reflect: true }) mobile = false;

  @query('.rows') private rows?: HTMLElement;

  /**
   * The element that actually scrolls.
   *
   * Exposed for the same reason `scrollRowIntoView` is a method: only the element
   * that scrolls can answer for it, and the workspace needs it to drive the band's
   * collapse. Undefined until the first render.
   */
  get scroller(): HTMLElement | undefined {
    return this.rows;
  }

  /**
   * Bring the selected row into view after a pin is clicked.
   *
   * Called by the workspace rather than derived from `selected`, deliberately:
   * selecting from the *list* must not scroll the list — the row is already
   * under the pointer, and moving it out from under a click is disorienting.
   *
   * Awaits its OWN render first, which is not optional: the workspace's
   * `updateComplete` says nothing about whether this component has re-rendered,
   * so a caller acting on a list that just changed would query for a row that
   * does not exist yet and silently do nothing.
   */
  /**
   * Back to the first row.
   *
   * Called when the sheet re-sorts around a new map centre (ADR 0012 decision
   * 5): the ordering is a fresh answer, so a preserved scroll position would be
   * pointing into the previous one.
   */
  async scrollToTop(): Promise<void> {
    await this.updateComplete;
    this.rows?.scrollTo({ top: 0 });
  }

  async scrollRowIntoView(mapPlaceId: number): Promise<void> {
    await this.updateComplete;
    const row = this.rows?.querySelector(`[data-id="${mapPlaceId}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  override render() {
    const n = this.venues.length;
    return html`
      <div class="rows">
        ${n === 0 && !this.loading
          ? html`<div class="empty">
              <g-notice>
                ${this.mobile || !this.follow
                  ? 'No places match these filters.'
                  : 'No places in this part of the map. Zoom out, or turn off “Update list as map moves”.'}
              </g-notice>
            </div>`
          : nothing}
        <!-- repeat(), not .map(): keyed on the venue so a row element stays with
             its venue. This list is re-filtered on every camera move, and
             unkeyed Lit reuses rows by index — so panning would hand row 3's
             element to a different venue along with whatever that element had
             accumulated: its remembered contain-intrinsic-size, its hover and
             focus, its active bar. -->
        ${repeat(
          this.venues,
          (v) => v.map_place_id,
          (v) => html`
            <place-row
              data-id=${v.map_place_id}
              .venue=${v}
              .mobile=${this.mobile}
              .active=${v.map_place_id === this.selected}
              @select=${() => this.emit('select', v.map_place_id)}
              @watch=${(e: CustomEvent<{ venueId: number; reelId: number }>) =>
                this.emit('watch', e.detail)}
              @toggle-visited=${() => this.emit('toggle-visited', v.map_place_id)}
              @remove=${() => this.emit('remove', v.map_place_id)}
            ></place-row>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'map-panel': MapPanel;
  }
}
